// Chart downloads (charts-15). The PNG is the chart's own SVG (recharts
// draws resolved colours, COLORS reads the tokens) on a white 2x canvas,
// with the chart's title above it and the key of the series on show below
// it (numbers-02): the legend on screen is HTML outside the chart's SVG.
export function chartFileName(title: string, ext: 'csv' | 'png'): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'chart';
  return `${slug}.${ext}`;
}

export type ChartKey = { title: string; series: { name: string; color: string }[] };

const NS = 'http://www.w3.org/2000/svg';
const INK = '#1e293b'; // text on the exported image's white paper
const PAD = 8;
const TITLE_BAND = 28;
const KEY_ROW = 18;
const SWATCH = 10;
// No layout to measure text in a detached SVG: an average glyph at 12px.
// A long series name is cut in the key so it cannot run past the image's
// edge; the CSV keeps the full name.
const KEY_NAME_MAX = 24;
const keyName = (name: string) => (name.length > KEY_NAME_MAX ? `${name.slice(0, KEY_NAME_MAX)}…` : name);
const charWidth = (size: number) => 6.8 * (size / 12);
const keyItemWidth = (name: string) => SWATCH + 5 + Math.ceil(name.length * charWidth(12)) + 14;
// The title, cut with an ellipsis where it would run past the image's width.
const TITLE_SIZE = 14;
function fitTitle(title: string, room: number): string {
  const max = Math.floor(room / charWidth(TITLE_SIZE));
  return title.length <= max ? title : `${title.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function text(x: number, y: number, size: number, value: string, attrs: Record<string, string> = {}): SVGTextElement {
  const t = document.createElementNS(NS, 'text');
  t.setAttribute('x', String(x));
  t.setAttribute('y', String(y));
  t.setAttribute('font-size', String(size));
  t.setAttribute('fill', INK);
  for (const [k, v] of Object.entries(attrs)) t.setAttribute(k, v);
  t.textContent = value;
  return t;
}

// The image's SVG: the title, the chart at its own size, then the key in
// centred rows that wrap at the chart's width.
export function exportSvg(svg: SVGSVGElement, width: number, height: number, key: ChartKey, fontFamily: string): { svg: SVGSVGElement; width: number; height: number } {
  const rows: { name: string; color: string; w: number }[][] = [];
  const maxRow = width - 2 * PAD;
  for (const s of key.series) {
    const name = keyName(s.name);
    const w = Math.min(keyItemWidth(name), maxRow);
    const row = rows[rows.length - 1];
    if (row && row.reduce((t, i) => t + i.w, 0) + w <= maxRow) row.push({ name, color: s.color, w });
    else rows.push([{ name, color: s.color, w }]);
  }
  const keyBand = rows.length ? rows.length * KEY_ROW + PAD : 0;
  const total = TITLE_BAND + height + keyBand;
  const out = document.createElementNS(NS, 'svg') as SVGSVGElement;
  out.setAttribute('xmlns', NS);
  out.setAttribute('width', String(width));
  out.setAttribute('height', String(total));
  out.setAttribute('viewBox', `0 0 ${width} ${total}`);
  // The serialized SVG carries no stylesheet: give its text the page's font.
  out.setAttribute('font-family', fontFamily);
  out.appendChild(text(PAD, PAD + 14, TITLE_SIZE, fitTitle(key.title, width - 2 * PAD), { 'font-weight': '600' }));
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('x', '0');
  clone.setAttribute('y', String(TITLE_BAND));
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  out.appendChild(clone);
  const g = document.createElementNS(NS, 'g');
  g.setAttribute('data-part', 'key');
  rows.forEach((row, r) => {
    const rowWidth = row.reduce((t, i) => t + i.w, 0);
    let x = Math.max(PAD, (width - rowWidth) / 2);
    const y = TITLE_BAND + height + r * KEY_ROW + PAD;
    for (const item of row) {
      const sw = document.createElementNS(NS, 'rect');
      sw.setAttribute('x', String(x));
      sw.setAttribute('y', String(y));
      sw.setAttribute('width', String(SWATCH));
      sw.setAttribute('height', String(SWATCH));
      sw.setAttribute('rx', '2');
      sw.setAttribute('fill', item.color);
      g.appendChild(sw);
      g.appendChild(text(x + SWATCH + 5, y + SWATCH - 1, 12, item.name));
      x += item.w;
    }
  });
  out.appendChild(g);
  return { svg: out, width, height: total };
}

export async function downloadChartPng(figure: HTMLElement, filename: string, key: ChartKey): Promise<boolean> {
  const svg = figure.querySelector('svg.recharts-surface');
  if (!(svg instanceof SVGSVGElement)) return false;
  const rect = svg.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const { svg: image, width, height } = exportSvg(svg, rect.width, rect.height, key, getComputedStyle(figure).fontFamily);
  const src = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(image)], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => { img.onload = () => resolve(); img.onerror = () => reject(new Error('svg')); img.src = src; });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width * 2);
    canvas.height = Math.ceil(height * 2);
    const g = canvas.getContext('2d');
    if (!g) return false;
    g.fillStyle = '#ffffff'; // the exported image's paper
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.scale(2, 2);
    g.drawImage(img, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return false;
    const out = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = out; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(out), 1000);
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(src);
  }
}
